import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useUserPreferences, ColumnsPreference } from '../../contexts/UserPreferencesContext'
import { StandardColumn } from './types'
import { buildDefaultColumnsState } from './columnUtils'

export function useColumnsState<T>(screenKey: string, columns: StandardColumn<T>[]) {
  const {
    getColumnsPreference,
    saveColumnsPreference,
    resetColumnsPreference,
    isLoading: isPrefsLoading,
  } = useUserPreferences()

  const defaultColumnsState = useMemo(() => buildDefaultColumnsState(columns), [columns])
  const [columnsState, setColumnsState] = useState<ColumnsPreference>(defaultColumnsState)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (isPrefsLoading) return
    setColumnsState(getColumnsPreference(screenKey, defaultColumnsState))
  }, [isPrefsLoading, screenKey, getColumnsPreference, defaultColumnsState])

  const handleColumnsChange = useCallback(
    (next: ColumnsPreference) => {
      setColumnsState(next)
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current)
      }
      saveTimerRef.current = setTimeout(() => {
        saveColumnsPreference(screenKey, next)
      }, 500)
    },
    [screenKey, saveColumnsPreference],
  )

  const handleRestoreColumns = useCallback(async () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
    }
    await resetColumnsPreference(screenKey)
  }, [screenKey, resetColumnsPreference])

  useEffect(() => () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
    }
  }, [])

  return { columnsState, handleColumnsChange, handleRestoreColumns, isPrefsLoading }
}
